// src/components/ChatWindow.js

import React, { useRef, useEffect, useState } from 'react'
import {
  List,
  ListItem,
  Box,
  Typography,
  IconButton,
  TextField,
  ListItemAvatar,
  Avatar
} from '@mui/material'
import EditIcon from '@mui/icons-material/Edit'
import DeleteIcon from '@mui/icons-material/Delete'
import CheckIcon from '@mui/icons-material/Check'
import CloseIcon from '@mui/icons-material/Close'
import ThumbUpIcon from '@mui/icons-material/ThumbUp'
import ReplyIcon from '@mui/icons-material/Reply'

// Çoklu dil desteği
import { useTranslation } from 'react-i18next'

export default function ChatWindow({
  messages = [],
  currentUser,
  style,
  onEditMessage,
  onDeleteMessage,
  onToggleLike,
  onReplyMessage,
  userRole
}) {
  const { t } = useTranslation()  // i18n Hook

  const dummyRef = useRef()
  const [editingMessageId, setEditingMessageId] = useState(null)
  const [updatedText, setUpdatedText] = useState('')

  // Yeni mesaj geldiğinde otomatik scroll
  useEffect(() => {
    dummyRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  // Mesaj düzenlemeye başlama
  const handleStartEditing = (message) => {
    setEditingMessageId(message.id)
    setUpdatedText(message.text || '')
  }

  // Düzenlemeyi iptal etme
  const handleCancelEditing = () => {
    setEditingMessageId(null)
    setUpdatedText('')
  }

  // Düzenlenen mesajı kaydetme
  const handleSaveEditing = async (messageId) => {
    try {
      await onEditMessage?.(messageId, updatedText)
      setEditingMessageId(null)
      setUpdatedText('')
    } catch (error) {
      console.error(t('chatWindow.updateError'), error)
    }
  }

  // Mesaj silme
  const handleDeleteMessage = async (messageId) => {
    if (window.confirm(t('chatWindow.deleteConfirm'))) {
      try {
        await onDeleteMessage?.(messageId)
      } catch (error) {
        console.error(t('chatWindow.deleteError'), error)
      }
    }
  }

  // Timestamp format
  const formatTimestamp = (timestamp) => {
    if (!timestamp) return ''
    if (typeof timestamp.toDate === 'function') {
      const dateObj = timestamp.toDate()
      return dateObj.toLocaleString()
    }
    return new Date(timestamp).toLocaleString()
  }

  return (
    <Box
      sx={{
        height: '400px',
        overflowY: 'auto',
        border: '1px solid #ccc',
        borderRadius: '8px',
        p: 2,
        ...style
      }}
    >
      <List>
        {messages.map((msg) => {
          const isCurrentUser = msg.userId === currentUser?.uid
          const isEditing = editingMessageId === msg.id

          // Like butonu rengi
          const likedBy = msg.likedBy || []
          const hasCurrentUserLiked = likedBy.includes(currentUser?.uid)
          const likeCount = likedBy.length

          return (
            <ListItem
              key={msg.id}
              alignItems="flex-start"
              sx={{
                justifyContent: isCurrentUser ? 'flex-end' : 'flex-start',
              }}
            >
              {/* Avatar (eğer başkası gönderiyorsa solda) */}
              {!isCurrentUser && (
                <ListItemAvatar>
                  {msg.avatarUrl ? (
                    <Avatar alt={msg.userNickname} src={msg.avatarUrl} />
                  ) : (
                    <Avatar>
                      {msg.userNickname
                        ? msg.userNickname.charAt(0).toUpperCase()
                        : 'U'}
                    </Avatar>
                  )}
                </ListItemAvatar>
              )}

              <Box
                sx={{
                  backgroundColor: isCurrentUser ? '#cfe9ff' : '#f0f0f0',
                  p: 1,
                  borderRadius: '8px',
                  maxWidth: '60%',
                  display: 'flex',
                  flexDirection: 'column',
                  '@media (max-width: 600px)': {
                    maxWidth: '100%',
                  },
                }}
              >
                {/* Gönderen (e-posta / nick) */}
                {!isCurrentUser && (
                  <Typography variant="subtitle2" sx={{ color: '#555' }}>
                    {msg.userNickname || msg.userEmail}
                  </Typography>
                )}

                {/* Eğer bu mesaj başka bir mesaja cevap ise: (replyToId) */}
                {msg.replyToId && (
                  <Box
                    sx={{
                      backgroundColor: '#fff',
                      p: 1,
                      borderRadius: '4px',
                      mb: 1,
                      boxShadow: '0 0 3px rgba(0,0,0,0.1)'
                    }}
                  >
                    <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
                      {t('chatWindow.replyTitle')}{' '}
                      {msg.replyToText || t('chatWindow.deletedOrEmpty')}
                    </Typography>

                    {msg.replyToFileUrl && msg.replyToFileType?.startsWith('image/') && (
                      <Box sx={{ mt: 1 }}>
                        <img
                          src={msg.replyToFileUrl}
                          alt="reply-image-thumb"
                          style={{
                            width: '60px',
                            height: '60px',
                            objectFit: 'cover',
                            borderRadius: '4px'
                          }}
                        />
                      </Box>
                    )}
                    {msg.replyToFileUrl && msg.replyToFileType?.startsWith('video/') && (
                      <Box sx={{ mt: 1 }}>
                        <video
                          src={msg.replyToFileUrl}
                          style={{
                            width: '60px',
                            height: '60px',
                            objectFit: 'cover',
                            borderRadius: '4px'
                          }}
                          muted
                          autoPlay
                          loop
                        />
                      </Box>
                    )}
                  </Box>
                )}

                {/* Düzenleme modundaysak text alanı, değilse normal metin */}
                {isEditing ? (
                  <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                    <TextField
                      variant="outlined"
                      size="small"
                      value={updatedText}
                      onChange={(e) => setUpdatedText(e.target.value)}
                      fullWidth
                    />
                    <IconButton
                      color="primary"
                      onClick={() => handleSaveEditing(msg.id)}
                    >
                      <CheckIcon />
                    </IconButton>
                    <IconButton
                      color="error"
                      onClick={handleCancelEditing}
                    >
                      <CloseIcon />
                    </IconButton>
                  </Box>
                ) : (
                  <>
                    {msg.text && (
                      <Typography variant="body1" sx={{ mt: 1 }}>
                        {msg.text}
                      </Typography>
                    )}

                    {msg.fileUrl && msg.fileType && (
                      <Box sx={{ mt: 1 }}>
                        {msg.fileType.startsWith('image/') && (
                          <img
                            src={msg.fileUrl}
                            alt="uploaded-img"
                            style={{
                              maxWidth: '100%',
                              height: 'auto',
                              borderRadius: '8px',
                              objectFit: 'cover',
                            }}
                          />
                        )}
                        {msg.fileType.startsWith('video/') && (
                          <video
                            src={msg.fileUrl}
                            controls
                            style={{
                              maxWidth: '100%',
                              maxHeight: '400px',
                              borderRadius: '8px',
                              marginTop: '8px',
                              objectFit: 'contain',
                            }}
                          />
                        )}
                        {msg.fileType.startsWith('audio/') && (
                          <audio
                            src={msg.fileUrl}
                            controls
                            style={{ width: '100%', marginTop: '8px' }}
                          />
                        )}
                      </Box>
                    )}

                    {!msg.fileUrl && msg.imageUrl && (
                      <Box sx={{ mt: 1 }}>
                        <img
                          src={msg.imageUrl}
                          alt="uploaded-img"
                          style={{
                            maxWidth: '100%',
                            height: 'auto',
                            borderRadius: '8px',
                            objectFit: 'cover',
                          }}
                        />
                      </Box>
                    )}
                  </>
                )}

                {/* Tarih */}
                {msg.createdAt && (
                  <Typography
                    variant="caption"
                    sx={{ color: '#999', display: 'block', mt: 0.5 }}
                  >
                    {formatTimestamp(msg.createdAt)}
                  </Typography>
                )}

                {/* Alt kısım: Like, Reply, Edit, Delete */}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    mt: 1,
                    justifyContent: 'space-between'
                  }}
                >
                  {/* Like butonu */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <IconButton
                      size="small"
                      onClick={() => onToggleLike?.(msg.id)}
                      color={hasCurrentUserLiked ? 'primary' : 'default'}
                    >
                      <ThumbUpIcon fontSize="small" />
                    </IconButton>
                    <Typography variant="body2">{likeCount}</Typography>
                  </Box>

                  {/* Reply / Edit / Delete butonları */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <IconButton
                      size="small"
                      onClick={() => {
                        if (userRole !== 'observer') {
                          onReplyMessage?.(msg)
                        } else {
                          alert(t('chatWindow.observerReplyError'))
                        }
                      }}
                    >
                      <ReplyIcon fontSize="small" />
                    </IconButton>

                    {msg.userEmail === currentUser?.email && !isEditing && userRole !== 'observer' && (
                      <>
                        <IconButton
                          size="small"
                          onClick={() => handleStartEditing(msg)}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleDeleteMessage(msg.id)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </>
                    )}
                  </Box>
                </Box>
              </Box>

              {/* Avatar (kendi mesajıysa sağda) */}
              {isCurrentUser && (
                <ListItemAvatar>
                  {currentUser.avatarUrl ? (
                    <Avatar alt={currentUser.nickname} src={currentUser.avatarUrl} />
                  ) : (
                    <Avatar>
                      {currentUser.nickname
                        ? currentUser.nickname.charAt(0).toUpperCase()
                        : 'U'}
                    </Avatar>
                  )}
                </ListItemAvatar>
              )}
            </ListItem>
          )
        })}
        <div ref={dummyRef}></div>
      </List>
    </Box>
  )
}